'use client';

import { useState } from 'react';
import ResourceCard from './ResourceCard';
import SearchBar from './SearchBar';
import FilterPanel from './FilterPanel';
import FeaturedResource from './FeaturedResource';
import QuickAccessTools from './QuickAccessTools';
import Icon from '@/components/ui/AppIcon';


interface Resource {
  id: string;
  title: string;
  description: string;
  category: string;
  type: string;
  image: string;
  alt: string;
  downloadSize: string;
  lastUpdated: string;
  downloads: number;
  rating: number;
  tags: string[];
  materials?: string[];
  processes?: string[];
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface Tool {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
}

interface ResourcesInteractiveProps {
  initialResources: Resource[];
  featuredResource: {
    title: string;
    description: string;
    image: string;
    alt: string;
    category: string;
    downloadSize: string;
    downloads: number;
  };
  categories: FilterOption[];
  types: FilterOption[];
  materials: FilterOption[];
  processes: FilterOption[];
  tools: Tool[];
}

export default function ResourcesInteractive({
  initialResources,
  featuredResource,
  categories,
  types,
  materials,
  processes,
  tools,
}: ResourcesInteractiveProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [resources, setResources] = useState(initialResources);
  const [bookmarkedResources, setBookmarkedResources] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    categories: [] as string[],
    types: [] as string[],
    materials: [] as string[],
    processes: [] as string[],
  });
  const [sortBy, setSortBy] = useState('relevance');

  useState(() => {
    setIsHydrated(true);
  });

  const handleSearch = (query: string) => {
    if (!isHydrated) return;
    setSearchQuery(query);
    filterResources(query, activeFilters, sortBy);
  };

  const handleFilterChange = (filters: typeof activeFilters) => {
    if (!isHydrated) return;
    setActiveFilters(filters);
    filterResources(searchQuery, filters, sortBy);
  };

  const handleSortChange = (newSortBy: string) => {
    if (!isHydrated) return;
    setSortBy(newSortBy);
    filterResources(searchQuery, activeFilters, newSortBy);
  };

  const filterResources = (
    query: string,
    filters: typeof activeFilters,
    sort: string
  ) => {
    let filtered = [...initialResources];

    if (query) {
      filtered = filtered.filter(
        (resource) =>
          resource.title.toLowerCase().includes(query.toLowerCase()) ||
          resource.description.toLowerCase().includes(query.toLowerCase()) ||
          resource.tags.some((tag) =>
            tag.toLowerCase().includes(query.toLowerCase())
          )
      );
    }

    if (filters.categories.length > 0) {
      filtered = filtered.filter((resource) =>
        filters.categories.includes(resource.category)
      );
    }

    if (filters.types.length > 0) {
      filtered = filtered.filter((resource) =>
        filters.types.includes(resource.type)
      );
    }

    if (filters.materials.length > 0) {
      filtered = filtered.filter((resource) =>
        resource.materials?.some((material) =>
          filters.materials.includes(material)
        )
      );
    }

    if (filters.processes.length > 0) {
      filtered = filtered.filter((resource) =>
        resource.processes?.some((process) =>
          filters.processes.includes(process)
        )
      );
    }

    switch (sort) {
      case 'downloads':
        filtered.sort((a, b) => b.downloads - a.downloads);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'recent':
        filtered.sort(
          (a, b) =>
            new Date(b.lastUpdated).getTime() -
            new Date(a.lastUpdated).getTime()
        );
        break;
      default:
        break;
    }

    setResources(filtered);
  };

  const handleDownload = (resourceId: string) => {
    if (!isHydrated) return;
    console.log('Downloading resource:', resourceId);
  };

  const handleBookmark = (resourceId: string) => {
    if (!isHydrated) return;
    setBookmarkedResources((prev) =>
      prev.includes(resourceId)
        ? prev.filter((id) => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const handleToolClick = (toolId: string) => {
    if (!isHydrated) return;
    console.log('Opening tool:', toolId);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="mx-auto px-6 lg:px-8 py-12">
          <div className="h-96 bg-muted rounded-lg mb-12"></div>
          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <div className="h-96 bg-muted rounded-lg"></div>
            </div>
            <div className="lg:col-span-3">
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-96 bg-muted rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="mx-auto px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h1 className="font-headline text-4xl md:text-5xl font-bold text-foreground mb-4">
              Engineering Resources Hub
            </h1>
            <p className="font-body text-lg text-technical-gray max-w-3xl mx-auto">
              Comprehensive technical library with manufacturing guides, material specifications, and compliance documentation
            </p>
          </div>

          <div className="max-w-4xl mx-auto mb-8">
            <SearchBar onSearch={handleSearch} />
          </div>

          <FeaturedResource resource={featuredResource} />
        </div>

        {/* Quick Access Tools */}
        <div className="mb-12">
          <QuickAccessTools tools={tools} onToolClick={handleToolClick} />
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <FilterPanel
                categories={categories}
                types={types}
                materials={materials}
                processes={processes}
                onFilterChange={handleFilterChange}
              />
            </div>
          </div>

          {/* Resources Grid */}
          <div className="lg:col-span-3">
            {/* Sort and Results Count */}
            <div className="flex items-center justify-between mb-6">
              <div className="font-body text-sm text-technical-gray">
                Showing {resources.length} resources
              </div>

              <div className="flex items-center space-x-2">
                <span className="font-body text-sm text-technical-gray">
                  Sort by:
                </span>
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-4 py-2 bg-input text-foreground font-body text-sm rounded-lg border border-border focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
                >
                  <option value="relevance">Relevance</option>
                  <option value="downloads">Most Downloaded</option>
                  <option value="rating">Highest Rated</option>
                  <option value="recent">Recently Updated</option>
                </select>
              </div>
            </div>

            {/* Resources Grid */}
            {resources.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {resources.map((resource) => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    onDownload={handleDownload}
                    onBookmark={handleBookmark}
                    isBookmarked={bookmarkedResources.includes(resource.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                  <Icon
                    name="DocumentMagnifyingGlassIcon"
                    size={32}
                    className="text-technical-gray"
                  />
                </div>
                <h3 className="font-headline text-xl font-semibold text-foreground mb-2">
                  No resources found
                </h3>
                <p className="font-body text-sm text-technical-gray">
                  Try adjusting your search or filters
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}